import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { VatStatusViewStore } from "../../store";
import { VatStatusView } from "../view/VatStatusView";
import { VatStatusUpdateStore } from "../../store";
import { VatStatusUpdate } from "../update/VatStatusUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type VatStatusModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const VatStatusModalContent: React.FC<VatStatusModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new VatStatusUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new VatStatusViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <VatStatusView
            vatStatusViewStore={storeInfo.store as VatStatusViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <VatStatusUpdate
            vatStatusUpdateStore={storeInfo.store as VatStatusUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
